import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Info,
  Loader2,
  Sparkles,
  UtensilsCrossed,
  Wand2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { generateItinerary } from '@/api/itinerary.api';
import { buildPreviewRows } from '@/lib/itineraryPreview';
import { ApiError } from '@/types/api';
import type { ItineraryConfig, ItineraryPlan, SchedulableItem } from '@/types/itinerary';
import { ItinerarySummary } from './ItinerarySummary';
import { ItineraryTimeline, TimelineEndCap } from './ItineraryTimeline';

interface SmartItineraryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The ticket this seat belongs to (header context). */
  ticketName: string;
  /** 1-based seat number shown as "#N". */
  displayNumber?: number;
  /** Schedulable wahana for this seat, in visit order (each is quantity-1). */
  schedulableItems: SchedulableItem[];
  /** 'YYYY-MM-DD' for the generate payload. */
  visitDate: string;
  /** The entry ticket's selected slot start ('HH:mm'), for the default start. */
  entrySlotStart?: string;
  /** Receives the backend plan + the config the cashier chose. */
  onApply: (plan: ItineraryPlan, config: ItineraryConfig) => void;
}

const DEFAULT_START_TIME = '10:00';
const GAP_OPTIONS = [0, 5, 10, 15, 20, 30];
const LUNCH_DURATION_OPTIONS = [45, 60, 75, 90, 95, 120];

const pad2 = (n: number) => String(n).padStart(2, '0');

function toMinutes(hhmm: string): number | null {
  if (typeof hhmm !== 'string' || !hhmm.includes(':')) return null;
  const parts = hhmm.split(':');
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

/**
 * Whether an 'HH:mm' time falls in the future on `visitDate` — the visit date
 * is a later day, or it's today and the time hasn't passed. Local time.
 */
function isFutureOnDate(hhmm: string, visitDate: string): boolean {
  const min = toMinutes(hhmm);
  if (min == null || !visitDate) return false;
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  if (visitDate > todayStr) return true;
  if (visitDate === todayStr) return min > now.getHours() * 60 + now.getMinutes();
  return false;
}

/**
 * Default start time. Prefer the entry ticket's slot start when still future;
 * else "now + 10 min" for a today visit; else the 10:00 park-open default.
 */
function computeDefaultStartTime(visitDate: string, entrySlotStart?: string): string {
  if (entrySlotStart && isFutureOnDate(entrySlotStart, visitDate)) return entrySlotStart;
  if (!visitDate) return DEFAULT_START_TIME;
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  if (visitDate !== todayStr) return DEFAULT_START_TIME;
  const plus10 = new Date(now.getTime() + 10 * 60 * 1000);
  const spilledOver =
    plus10.getFullYear() !== now.getFullYear() ||
    plus10.getMonth() !== now.getMonth() ||
    plus10.getDate() !== now.getDate();
  if (spilledOver) return '23:59';
  return `${pad2(plus10.getHours())}:${pad2(plus10.getMinutes())}`;
}

/**
 * Whether the lunch / rest break is FULLY in the past — its whole window has
 * elapsed relative to the plan start, or (for a same-day visit) the clock. When
 * true it is excluded from the generated plan.
 */
function isLunchPast(
  lunchAfter: string,
  lunchDuration: number,
  startTime: string,
  visitDate: string,
): boolean {
  const lunchMin = toMinutes(lunchAfter);
  if (lunchMin == null) return false;
  const windowEnd = lunchMin + (Number.isFinite(lunchDuration) ? lunchDuration : 0);
  const startMin = toMinutes(startTime);
  if (startMin != null && windowEnd <= startMin) return true;
  if (visitDate) {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
    if (visitDate === todayStr) {
      const nowMin = now.getHours() * 60 + now.getMinutes();
      if (windowEnd <= nowMin) return true;
    }
  }
  return false;
}

/** Map the typed ApiError → friendly Indonesian copy. */
function mapGenError(err: unknown): string {
  const code = err instanceof ApiError ? err.code : undefined;
  const errorCode = err instanceof ApiError ? err.errorCode : null;
  if (code === 401) return 'Sesi kamu telah berakhir. Silakan masuk kembali.';
  if (code === 429 || errorCode === 'RATE_LIMITED')
    return 'Terlalu banyak permintaan. Mohon tunggu sejenak lalu coba lagi.';
  if (code === 422 || errorCode === 'ATTRACTION_NOT_FOUND_OR_INELIGIBLE')
    return 'Beberapa wahana tidak lagi tersedia. Perbarui pilihanmu lalu coba lagi.';
  if (code === 400 || errorCode === 'VALIDATION_ERROR')
    return 'Pengaturan tidak valid. Periksa kembali waktu dan jeda.';
  return 'Gagal menyusun jadwal. Mohon coba lagi sebentar lagi.';
}

/**
 * SmartItineraryDialog — a per-seat "Atur Jadwal Pintar" mini-builder. The
 * cashier tunes start time / gap / lunch, generates a backend-authoritative
 * plan, previews it, then applies it (the page books each wahana against the
 * slot the backend assigned). Ported from the reference `TicketItineraryDialog`,
 * re-skinned to shadcn/Tailwind, no i18n (inline Indonesian).
 */
export function SmartItineraryDialog({
  open,
  onOpenChange,
  ticketName,
  displayNumber,
  schedulableItems,
  visitDate,
  entrySlotStart,
  onApply,
}: SmartItineraryDialogProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [startTime, setStartTime] = useState(DEFAULT_START_TIME);
  const [gapMinutes, setGapMinutes] = useState(15);
  const [lunchEnabled, setLunchEnabled] = useState(true);
  const [lunchAfter, setLunchAfter] = useState('11:30');
  const [lunchDuration, setLunchDuration] = useState(60);

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ItineraryPlan | null>(null);

  // Reset transient state + recompute the default start time on every open so a
  // stale preview from a previous seat never leaks in.
  useEffect(() => {
    if (open) {
      setResult(null);
      setError(null);
      setStartTime(computeDefaultStartTime(visitDate, entrySlotStart));
    }
  }, [open, visitDate, entrySlotStart]);

  // Only items carrying an attractionId can be scheduled by the backend. The
  // caller pre-filters, but guard here too (order preserved).
  const generatable = useMemo(
    () => schedulableItems.filter((it) => it.attractionId),
    [schedulableItems],
  );
  const generatableUnits = useMemo(
    () => generatable.reduce((n, it) => n + Math.max(1, Number(it.quantity) || 1), 0),
    [generatable],
  );

  const lunchPast = lunchEnabled && isLunchPast(lunchAfter, lunchDuration, startTime, visitDate);

  const previewRows = useMemo(() => buildPreviewRows(result), [result]);
  const summary = result?.summary ?? null;

  const handleGenerate = async () => {
    if (generatable.length === 0) {
      setError('Tidak ada wahana yang bisa dijadwalkan otomatis untuk tiket ini.');
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      // Exclude lunch only when its whole window has already passed. Recomputed
      // here so a slow form session still reflects the current time.
      const includeLunch =
        lunchEnabled && !isLunchPast(lunchAfter, lunchDuration, startTime, visitDate);
      const plan = await generateItinerary({
        // Expand by quantity (all 1 here) — one stop per unit.
        attractionIds: generatable.flatMap((it) =>
          Array.from({ length: Math.max(1, Number(it.quantity) || 1) }, () =>
            String(it.attractionId),
          ),
        ),
        date: visitDate,
        startTime,
        gapMinutes: Number(gapMinutes),
        lunch: {
          enabled: includeLunch,
          startAfter: lunchAfter,
          durationMinutes: Number(lunchDuration),
        },
      });
      setResult(plan);
    } catch (err) {
      setError(mapGenError(err));
    } finally {
      setGenerating(false);
    }
  };

  const handleApply = () => {
    if (!result) return;
    const config: ItineraryConfig = {
      startTime,
      gapMinutes: Number(gapMinutes),
      lunch: {
        enabled: lunchEnabled,
        startAfter: lunchAfter,
        durationMinutes: Number(lunchDuration),
      },
      attractionIds: generatable.map((it) => String(it.attractionId)),
    };
    onApply(result, config);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[88vh] max-w-lg flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b border-border bg-card px-5 py-4">
          <span className="mb-1 inline-flex w-fit items-center gap-1 rounded-full border border-gold-500/50 bg-gold-200/30 px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-widest text-primary">
            <Sparkles className="h-3 w-3" /> Smart Itinerary
          </span>
          <DialogTitle>Atur Jadwal Pintar</DialogTitle>
          <DialogDescription>
            {ticketName}
            {displayNumber != null && (
              <span className="tnum ml-1 font-bold text-primary">#{displayNumber}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Body (scrollable). */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 pos-scroll">
          {/* Schedulable summary. */}
          <div className="mb-4 flex items-center gap-2 rounded-md border border-border bg-secondary px-3 py-2.5">
            <Info className="h-4 w-4 shrink-0 text-primary" />
            <p className="text-sm font-semibold leading-snug text-primary">
              {generatableUnits > 0
                ? `${generatableUnits} wahana akan dijadwalkan otomatis`
                : 'Tidak ada wahana yang bisa dijadwalkan otomatis'}
            </p>
          </div>

          {/* Settings header + advanced toggle. */}
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Wand2 className="h-4 w-4" />
            </span>
            <span className="text-sm font-bold text-ink-900">Pengaturan</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="ml-auto h-7 w-7 text-primary"
              onClick={() => setAdvancedOpen((o) => !o)}
              aria-expanded={advancedOpen}
              aria-label={advancedOpen ? 'Sembunyikan pengaturan' : 'Tampilkan pengaturan'}
            >
              {advancedOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="itin-start" className="flex items-center gap-1.5 text-xs">
              <Clock className="h-3.5 w-3.5 text-gold-600" /> Waktu mulai
            </Label>
            <Input
              id="itin-start"
              type="time"
              className="h-10"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </div>

          {advancedOpen && (
            <div className="mt-4 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Jeda antar wahana</Label>
                <Select value={String(gapMinutes)} onValueChange={(v) => setGapMinutes(Number(v))}>
                  <SelectTrigger className="h-10" aria-label="Jeda antar wahana">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GAP_OPTIONS.map((g) => (
                      <SelectItem key={g} value={String(g)}>
                        {g} menit
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-md border border-border bg-secondary p-3">
                <div className="flex items-center gap-2">
                  <UtensilsCrossed className="h-4 w-4 text-primary" />
                  <span className="text-sm font-bold text-ink-900">
                    Jeda Istirahat / Makan Siang
                  </span>
                  <Switch
                    className="ml-auto"
                    checked={lunchEnabled}
                    onCheckedChange={setLunchEnabled}
                    aria-label="Aktifkan jeda istirahat"
                  />
                </div>
                {lunchPast && (
                  <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-ink-400">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    Waktu istirahat sudah lewat — jeda ini tidak akan dimasukkan ke jadwal.
                  </p>
                )}
                {lunchEnabled && (
                  <div className="mt-3 flex gap-3">
                    <div className="flex-1 space-y-1.5">
                      <Label htmlFor="itin-lunch-after" className="text-xs">
                        Mulai setelah
                      </Label>
                      <Input
                        id="itin-lunch-after"
                        type="time"
                        className="h-10"
                        value={lunchAfter}
                        onChange={(e) => setLunchAfter(e.target.value)}
                      />
                    </div>
                    <div className="flex-1 space-y-1.5">
                      <Label className="text-xs">Durasi</Label>
                      <Select
                        value={String(lunchDuration)}
                        onValueChange={(v) => setLunchDuration(Number(v))}
                      >
                        <SelectTrigger className="h-10" aria-label="Durasi istirahat">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LUNCH_DURATION_OPTIONS.map((d) => (
                            <SelectItem key={d} value={String(d)}>
                              {d} menit
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Generate. */}
          <Button
            type="button"
            variant="gold"
            className="mt-5 w-full"
            onClick={handleGenerate}
            disabled={generating || generatable.length === 0}
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Menyusun jadwal…
              </>
            ) : (
              <>
                <Wand2 className="h-4 w-4" />
                {result ? 'Susun Ulang Jadwal' : 'Buat Jadwal Otomatis'}
              </>
            )}
          </Button>

          {error && (
            <div
              role="alert"
              className="mt-4 flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2.5"
            >
              <AlertCircle className="h-5 w-5 shrink-0 text-destructive" />
              <p className="text-sm font-medium text-destructive">{error}</p>
            </div>
          )}

          {/* Result preview. */}
          {result && (
            <div className="mt-5 animate-reveal-up">
              <div className="mb-4">
                <ItinerarySummary summary={summary} />
              </div>
              <div className="rounded-lg border border-border bg-card px-3 py-4">
                <ItineraryTimeline items={previewRows} />
                <TimelineEndCap summary={summary} />
              </div>
            </div>
          )}
        </div>

        {/* Footer actions. */}
        <DialogFooter className="border-t border-border bg-cream-50/50 px-5 py-4 sm:justify-end">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Batal
          </Button>
          <Button type="button" variant="gold" onClick={handleApply} disabled={!result}>
            Terapkan jadwal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
